import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Link,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import Loading from "../../components/Loading";
import useApi from "../../hooks/useApi";
import { toast } from "react-toastify";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { getContentLabel } from "../../utils/utils";
import PreImportFiles from "./PreImportFiles";

export default function PreImport({
  isFailed,
  errorMessage,
  source,
  sourceConfig,
}) {
  const { importLibrary } = useApi();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const libraryLabel =
    sourceConfig.libraryLabel ??
    sourceConfig.contentType
      .map((type) => getContentLabel({ type, plural: true, lowercase: true }))
      .join(" and ");

  // Seed option state from each option's declared default
  const [optionValues, setOptionValues] = useState(() => {
    const defaults = {};
    for (const opt of sourceConfig.options ?? []) {
      defaults[opt.key] = opt.default;
    }
    return defaults;
  });

  const handleOptionChange = (key, value) => {
    setOptionValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (
      !file ||
      !file.name.endsWith(sourceConfig.upload.accept.replace(".", ""))
    ) {
      toast.error(`Please upload a valid ${sourceConfig.upload.accept} file`, {
        autoClose: false,
      });
      return;
    }

    await runImport(file);
  };

  // entries: [{ file, type }] — from PreImportFiles, for multi-file sources.
  const handleMultiFilesStart = async (entries) => {
    await runImport(entries);
  };

  const runImport = async (fileOrEntries) => {
    try {
      setIsLoading(true);
      await importLibrary(fileOrEntries, source, optionValues);
      queryClient.invalidateQueries(["importSummary", source]);
    } catch (error) {
      console.error("Import failed", error);
      toast.error(
        "There was a problem importing your file, please try again later. If the problem persists, please contact us",
        { autoClose: false },
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Typography>
        You can import all the {libraryLabel} you've tracked on{" "}
        {sourceConfig.label}
        {sourceConfig.listLabel
          ? ` as well as any ${sourceConfig.listLabel} you've created`
          : ""}{" "}
        by following the steps below:
      </Typography>

      <Box component="ol" sx={{ pl: 3, mt: 1, mb: 0 }}>
        {sourceConfig.instructions.map((instruction, i) => (
          <Typography key={i} component="li" gutterBottom>
            {instruction.type === "link" ? (
              <Link
                href={instruction.href}
                target="_blank"
                rel="noopener noreferrer"
              >
                {instruction.text}
              </Link>
            ) : (
              instruction.text
            )}
          </Typography>
        ))}
      </Box>

      {/* Per-source import options (e.g. Storygraph rating rounding, note visibility) */}
      {sourceConfig.options?.length > 0 && (
        <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          {sourceConfig.options.map((opt) => (
            <FormControl key={opt.key} component="fieldset">
              {/* focused={false}: without this, FormLabel turns primary.main as soon as
                  a Radio below it is clicked (it inherits "focused" from FormControl
                  context) — this label is a static question, not an input label. */}
              <FormLabel
                component="legend"
                focused={false}
                sx={{ mb: 0.5, fontWeight: 500 }}
              >
                {opt.label}
              </FormLabel>
              <RadioGroup
                // A choice-level caption (e.g. "Recommended — ...") reads best on
                // its own line under that choice, so switch off the row layout
                // whenever any choice in this option has one.
                row={!opt.choices.some((choice) => choice.caption)}
                name={opt.key}
                value={optionValues[opt.key] ?? opt.default}
                onChange={(e) => handleOptionChange(opt.key, e.target.value)}
              >
                {opt.choices.map((choice) => (
                  <Box key={choice.value}>
                    <FormControlLabel
                      value={choice.value}
                      control={<Radio size="small" />}
                      label={choice.label}
                    />
                    {choice.caption && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", ml: 4, mt: -0.5 }}
                      >
                        {choice.caption}
                      </Typography>
                    )}
                  </Box>
                ))}
              </RadioGroup>
              {opt.helpText && (
                <FormHelperText sx={{ mt: -0.5 }}>{opt.helpText}</FormHelperText>
              )}
            </FormControl>
          ))}
        </Box>
      )}

      {sourceConfig.upload.multiple ? (
        <Box mt={2} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {isLoading && <Loading inline />}
          <Box sx={{ flexGrow: 1 }}>
            <PreImportFiles
              sourceConfig={sourceConfig}
              isLoading={isLoading}
              onStart={handleMultiFilesStart}
            />
          </Box>
        </Box>
      ) : (
        <Box
          mt={2}
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 2,
            ml: 2,
          }}
        >
          {isLoading && <Loading inline />}
          <input
            accept={sourceConfig.upload.accept}
            id={`${source}-upload`}
            aria-label={`Upload your ${sourceConfig.label} export file`}
            type="file"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
          <label htmlFor={`${source}-upload`}>
            <Button variant="contained" component="span" disabled={isLoading}>
              {isLoading ? "Uploading…" : sourceConfig.upload.buttonText}
            </Button>
          </label>
        </Box>
      )}

      {isFailed && (
        <Typography sx={{ mt: 2 }}>
          <i>
            {/* A message from the server means we could name a cause the user can
                fix themselves — sending them to support would waste their time. */}
            ⚠️{" "}
            {errorMessage || (
              <>
                It looks like your import failed, please{" "}
                <Link
                  href="https://app.youform.com/forms/fghdoj5a"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  contact us
                </Link>{" "}
                and we'll look into it
              </>
            )}
          </i>
        </Typography>
      )}
    </>
  );
}
